import { useEffect } from "react";
import {
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  DEFAULT_OG_IMAGE_ALT,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_OG_IMAGE_HEIGHT,
  formatTitle,
  absoluteUrl,
  truncate,
  buildShareImageUrl,
  SHARE_IMAGE_WIDTH,
  SHARE_IMAGE_HEIGHT,
} from "./seoConfig";

/**
 * Keeps <title>, meta description, canonical, OG/Twitter tags and JSON-LD in sync
 * with the current route.
 *
 * NOTE: this runs in the browser only. Social crawlers (WhatsApp, Facebook, LinkedIn,
 * X) do NOT execute JavaScript, so they never see anything this hook does — they read
 * the HTML injected by netlify/edge-functions/seo.js. This hook exists for
 * (a) real users, so the tab title is right, and (b) Googlebot, which does render JS.
 * The two must therefore agree; both read from seoConfig.js.
 */

const setMeta = (attr, key, content) => {
  if (typeof document === "undefined") return;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);

  if (content === null || content === undefined || content === "") {
    if (el && el.dataset.seo === "runtime") el.remove();
    return;
  }

  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.dataset.seo = "runtime";
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  if (typeof document === "undefined") return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!href) {
    if (el && el.dataset.seo === "runtime") el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.dataset.seo = "runtime";
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (id, data) => {
  if (typeof document === "undefined") return;
  const existing = document.getElementById(id);
  if (!data) {
    if (existing) existing.remove();
    return;
  }
  const script = existing || document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(script);
};

export const useSeo = ({
  title,
  description,
  image,
  imageAlt,
  imageWidth,
  imageHeight,
  type = "website",
  canonicalPath,
  noindex = false,
  jsonLd = null,
  ready = true,
} = {}) => {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    if (!ready || typeof window === "undefined") return;

    const origin = window.location.origin;
    const path =
      canonicalPath || window.location.pathname + window.location.search;
    const url = absoluteUrl(origin, path);

    const fullTitle = formatTitle(title);
    const desc = truncate(description);
    const isDefaultImage = !image;
    const img = isDefaultImage
      ? absoluteUrl(origin, DEFAULT_OG_IMAGE)
      : buildShareImageUrl(origin, image);
    const isCdnImage = img.includes("/.netlify/images?");

    document.title = fullTitle;

    setMeta("name", "description", desc);
    setLink("canonical", url);
    setMeta(
      "name",
      "robots",
      noindex ? "noindex, follow" : "index, follow, max-image-preview:large",
    );

    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:type", type === "product" ? "product" : "website");
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", img);
    setMeta("property", "og:image:secure_url", img);
    setMeta("property", "og:image:alt", imageAlt || DEFAULT_OG_IMAGE_ALT);
    setMeta(
      "property",
      "og:image:width",
      isDefaultImage
        ? DEFAULT_OG_IMAGE_WIDTH
        : isCdnImage
          ? SHARE_IMAGE_WIDTH
          : imageWidth || null,
    );
    setMeta(
      "property",
      "og:image:height",
      isDefaultImage
        ? DEFAULT_OG_IMAGE_HEIGHT
        : isCdnImage
          ? SHARE_IMAGE_HEIGHT
          : imageHeight || null,
    );

    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", img);
    setMeta("name", "twitter:image:alt", imageAlt || DEFAULT_OG_IMAGE_ALT);
  }, [
    title,
    description,
    image,
    imageAlt,
    imageWidth,
    imageHeight,
    type,
    canonicalPath,
    noindex,
    ready,
  ]);

  useEffect(() => {
    if (!ready) return;
    setJsonLd("ld-page", jsonLd);
    return () => setJsonLd("ld-page", null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jsonLdKey, ready]);
};

export default useSeo;
